import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { apiRoutes } from "../../configs/apiRoutes";
import instance from "../../api/apiClient";

export type ModuleStructure = {
  [key: string]:
    | {
        [subKey: string]: string[] | ModuleStructure;
      }
    | string[];
};

type SettingsSliceState = {
  modules?: {
    [key: string]: ModuleStructure;
  };
  isModulesFetched: boolean;
  geoList: string[];
  currencies: string[];
  payoutCurrencies: string[];
  locales: string[];
  services: string[];
  countries: Country[];
};

interface Country {
  name: string;
  code: string;
}

export const getModules = createAsyncThunk("settings/modules", async () => {
  try {
    const result = await instance.get(apiRoutes.appModules);
    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const getGeoList = createAsyncThunk("settings/geo", async () => {
  try {
    const result = await instance.get(apiRoutes.appGeoList);
    return result?.data?.list;
  } catch (error) {
    return undefined;
  }
});

export const getCurrencyList = createAsyncThunk(
  "settings/currencies",
  async () => {
    try {
      const result = await instance.get(apiRoutes.appCurrencyList);
      return result?.data?.currencies;
    } catch (error) {
      return undefined;
    }
  },
);

export const getPayoutCurrencyList = createAsyncThunk(
  "settings/payout-currencies",
  async () => {
    try {
      const result = await instance.get(apiRoutes.appPayoutCurrencyList);
      return result?.data?.payoutCurrencies;
    } catch (error) {
      return undefined;
    }
  },
);

export const getLocalesList = createAsyncThunk("settings/locales", async () => {
  try {
    const result = await instance.get(apiRoutes.appLocalesList);
    return result?.data?.locales;
  } catch (error) {
    return undefined;
  }
});

export const getServicesList = createAsyncThunk(
  "settings/services",
  async () => {
    try {
      const result = await instance.get(apiRoutes.appServicesList);
      return result?.data?.services;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCountryList = createAsyncThunk(
  "settings/countries",
  async () => {
    try {
      const result = await instance.get(apiRoutes.appCountryList);
      return result?.data?.countries;
    } catch (error) {
      return undefined;
    }
  },
);

/** A missing common-config list is valid and must stay an empty array. */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

const initialState: SettingsSliceState = {
  isModulesFetched: false,
  geoList: [],
  currencies: [],
  payoutCurrencies: [],
  locales: [],
  services: [],
  countries: [],
};

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getModules.fulfilled, (state, action) => {
      state.isModulesFetched = true;
      state.modules = action.payload;
    });
    builder.addCase(getGeoList.fulfilled, (state, action) => {
      state.geoList = asList(action.payload);
    });
    builder.addCase(getCurrencyList.fulfilled, (state, action) => {
      state.currencies = asList(action.payload);
    });
    builder.addCase(getPayoutCurrencyList.fulfilled, (state, action) => {
      state.payoutCurrencies = asList(action.payload);
    });
    builder.addCase(getLocalesList.fulfilled, (state, action) => {
      state.locales = asList(action.payload);
    });
    builder.addCase(getServicesList.fulfilled, (state, action) => {
      state.services = asList(action.payload);
    });
    builder.addCase(getCountryList.fulfilled, (state, action) => {
      state.countries = asList(action.payload);
    });
  },
});

// export const { setIsAuthorized } = settingsSlice.actions;
